import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type UnsubscribeStatus = 'idle' | 'loading' | 'success' | 'error';

function isMarketingHostname(hostname: string) {
  return hostname === 'marketing.rightawayhome.com' || hostname === 'localhost' || hostname === '127.0.0.1';
}

function normalizeEmail(raw: string | null): string | null {
  const email = String(raw ?? '').trim().toLowerCase();
  if (!email) return null;
  if (!email.includes('@') || email.startsWith('@') || email.endsWith('@')) return null;
  return email;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) return `**@${domain}`;
  return `${local.slice(0, 2)}***@${domain}`;
}

function getCompanyLabel(companyRaw: string | null): string | null {
  const c = String(companyRaw ?? '').trim().toLowerCase();
  if (!c) return null;

  if (c === 'climate') return 'Climate Control Services';
  if (c === 'hales-ac' || c === 'hales') return 'Hales AC';
  if (c === 'abraham-ac' || c === 'abraham') return 'Abraham AC';

  return null;
}

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();

  const email = useMemo(() => normalizeEmail(searchParams.get('email')), [searchParams]);
  const companyRaw = useMemo(() => searchParams.get('company'), [searchParams]);
  const companyLabel = useMemo(() => getCompanyLabel(companyRaw), [companyRaw]);

  const [status, setStatus] = useState<UnsubscribeStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const { hostname } = window.location;
    if (!isMarketingHostname(hostname)) {
      setStatus('error');
      setError('This domain is not served by the Marketing portal.');
      return;
    }

    if (!email) {
      setStatus('error');
      setError('Missing or invalid email.');
      return;
    }

    let cancelled = false;

    async function run() {
      setStatus('loading');
      setError(null);

      try {
        const resp = await fetch('/api/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            company: companyRaw,
          }),
        });

        const payload = await resp.json().catch(() => null);
        if (!resp.ok) {
          throw new Error(payload?.error || payload?.message || 'Unsubscribe failed');
        }

        if (cancelled) return;
        setStatus('success');
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [email, companyRaw]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-xl font-semibold text-gray-900">Unsubscribe</h1>

          {companyLabel && (
            <p className="mt-2 text-sm text-gray-600">
              {companyLabel}
            </p>
          )}

          {status === 'loading' && (
            <div className="mt-6">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                <p className="text-gray-700">Processing your request…</p>
              </div>
              <p className="mt-2 text-sm text-gray-500">Email: {email ? maskEmail(email) : ''}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="mt-6">
              <p className="text-gray-900 font-medium">You’re unsubscribed.</p>
              <p className="mt-2 text-sm text-gray-600">
                We’ll stop sending marketing emails to {email ? maskEmail(email) : 'this address'}.
              </p>
              <p className="mt-3 text-sm text-gray-500">
                If you keep receiving messages, it may take a short time for systems to fully sync.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="mt-6">
              <p className="text-gray-900 font-medium">We couldn’t process this request.</p>
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              <p className="mt-3 text-sm text-gray-500">
                If you need help, please contact support.
              </p>
            </div>
          )}

          {status === 'idle' && (
            <p className="mt-6 text-sm text-gray-600">
              Loading…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
